import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Box,
  Stack,
  Typography,
  Stepper,
  Step,
  StepLabel,
  Alert
} from '@mui/material';
import UserDetailsModal from 'components/OrganizationSettings/UserDetailsModal';
import { signUserUp } from 'services/auth';
import cognito_groups from 'constants/cognito_groups';
import { logActivity, AUDIT_ACTIONS } from 'utils/auditLogger';

const AddUserWithOrgDialog = ({ 
  open, 
  onClose, 
  organizations, 
  clients, 
  loggedInUser,
  onUserAdded,
  loading 
}) => {
  const [step, setStep] = useState(0);
  const [selectedOrganization, setSelectedOrganization] = useState('');
  const [selectedClient, setSelectedClient] = useState('');
  const [showUserModal, setShowUserModal] = useState(false);
  const [filteredClients, setFilteredClients] = useState([]);
  const [submitting, setSubmitting] = useState(false);

  // Reset when dialog opens/closes
  useEffect(() => {
    if (!open) {
      setStep(0);
      setSelectedOrganization('');
      setSelectedClient('');
      setShowUserModal(false);
    }
  }, [open]);

  // Filter clients based on selected organization
  useEffect(() => {
    if (selectedOrganization) {
      const org = organizations[selectedOrganization];
      
      // Check if this is an affiliate/agency organization
      const isAffiliate = org?.type === 'AGENCY' || org?.isAgency || 
                         (org?.name && org.name.toUpperCase().startsWith('AFFILIATE'));
      
      if (isAffiliate) {
        // Find clients that belong to this affiliate
        const affiliateClients = Object.values(clients).filter(client => 
          client.agencyOrganizationID === selectedOrganization
        );
        console.log('Selected org:', selectedOrganization, 'Type:', org?.type, 'isAgency:', org?.isAgency, 'Name:', org?.name);
        console.log('Found clients for affiliate:', affiliateClients);
        setFilteredClients(affiliateClients);
      } else {
        setFilteredClients([]);
      }
    } else {
      setFilteredClients([]);
    }
  }, [selectedOrganization, organizations, clients]);

  // Get organization type
  const getOrganizationType = (orgId) => {
    const org = organizations[orgId];
    console.log('getOrganizationType - orgId:', orgId, 'org:', org);
    if (!org) return null;
    
    // Check various ways an organization can be marked as an affiliate
    if (org.type === 'SYSTEM' || org.id === 'SYSTEM') return 'system';
    if (org.type === 'AGENCY' || org.isAgency) return 'affiliate';
    if (org.type === 'CLIENT') return 'client';
    
    // Additional check: if the organization name starts with "AFFILIATE"
    if (org.name && org.name.toUpperCase().startsWith('AFFILIATE')) {
      console.log('Detected as affiliate based on name');
      return 'affiliate';
    }
    
    // Check if it's a client by looking in clients table
    const isClient = Object.values(clients).some(c => c.organizationID === orgId);
    console.log('Is in clients table?', isClient);
    return isClient ? 'client' : 'standalone';
  };

  // Determine if we need client selection
  const needsClientSelection = () => {
    const orgType = getOrganizationType(selectedOrganization);
    // Show client selection for affiliates even if they have no clients
    // This allows creating users at the affiliate level
    return orgType === 'affiliate';
  };

  // Get available roles based on organization type
  const getAvailableRoles = () => {
    const finalOrgId = selectedClient || selectedOrganization;
    const orgType = getOrganizationType(finalOrgId);
    
    const systemRoles = [{ label: 'System Admin', value: cognito_groups.SystemAdmin }];
    const agencyRoles = [{ label: 'Agency Admin', value: cognito_groups.AgencyAdmin }];
    const clientRoles = [
      { label: 'Client Admin', value: cognito_groups.ClientAdmin },
      { label: 'Client Viewer', value: cognito_groups.ClientViewer },
      { label: 'Project Manager', value: cognito_groups.ClientPMRole },
      { label: 'Subcontractor', value: cognito_groups.Subcontractor }
    ];
    
    if (orgType === 'system') {
      return systemRoles;
    } else if (orgType === 'affiliate') {
      // If we're creating a user at the affiliate level
      if (!selectedClient) {
        return agencyRoles;
      } else {
        // Creating user at client level under this affiliate
        return clientRoles;
      }
    } else {
      return clientRoles;
    }
  };

  const handleNext = () => {
    if (step === 0 && selectedOrganization) {
      const orgType = getOrganizationType(selectedOrganization);
      console.log('HandleNext - Selected org:', selectedOrganization, 'Type:', orgType);
      console.log('Needs client selection?', needsClientSelection());
      
      if (needsClientSelection()) {
        setStep(1); // Go to client selection
      } else {
        setShowUserModal(true); // Skip to user details
      }
    } else if (step === 1) {
      // Always allow proceeding from step 1 (client selection is optional)
      setShowUserModal(true); // Go to user details
    }
  };

  const handleBack = () => {
    if (step === 1) {
      setStep(0);
      setSelectedClient('');
    }
  };

  const handleUserSubmit = async (userData) => {
    setSubmitting(true);
    try {
      // Determine final organization ID
      const finalOrgId = selectedClient 
        ? organizations[selectedClient]?.id 
        : selectedOrganization;
      
      // Determine the appropriate Cognito group based on role
      const cognitoGroup = userData.role || cognito_groups.ClientViewer;
      
      try {
        // Try the normal flow with Cognito invitation
        await signUserUp(
          userData,
          cognitoGroup,
          finalOrgId,
          (newUser) => {
            // Build organization hierarchy string for logging
            let orgHierarchy = '';
            if (selectedClient) {
              const affiliate = organizations[selectedOrganization];
              const client = organizations[selectedClient];
              orgHierarchy = `${affiliate?.name || selectedOrganization} → ${client?.name || selectedClient}`;
            } else {
              const org = organizations[selectedOrganization];
              orgHierarchy = org?.name || selectedOrganization;
            }
            
            // Log the activity
            logActivity({
              entity: 'USER',
              entityID: newUser?.id,
              action: AUDIT_ACTIONS.USER_CREATED,
              field: 'user',
              value: JSON.stringify({
                email: userData.email,
                name: `${userData.firstName || ''} ${userData.lastName || ''}`.trim(),
                role: userData.role,
                organization: orgHierarchy
              }),
              user: loggedInUser
            });
          }
        );
      } catch (networkError) {
        // If there's a network error with the Lambda function, create user directly
        console.warn('AdminQueries API failed, creating user without email invitation:', networkError);
        
        // Import the createUSER mutation
        const { generateClient } = await import('aws-amplify/api');
        const { createUSER } = await import('graphql/mutations');
        const API = generateClient();
        
        // Create user directly in the database
        const result = await API.graphql({
          query: createUSER,
          variables: {
            input: {
              ...userData,
              organizationID: finalOrgId,
              role: cognitoGroup,
              status: 'PENDING' // Mark as pending since no invitation was sent
            }
          }
        });
        
        const newUser = result?.data?.createUSER;
        
        // Log the activity
        let orgHierarchy = '';
        if (selectedClient) {
          const affiliate = organizations[selectedOrganization];
          const client = organizations[selectedClient];
          orgHierarchy = `${affiliate?.name || selectedOrganization} → ${client?.name || selectedClient}`;
        } else {
          const org = organizations[selectedOrganization];
          orgHierarchy = org?.name || selectedOrganization;
        }
        
        logActivity({
          entity: 'USER',
          entityID: newUser?.id,
          action: AUDIT_ACTIONS.USER_CREATED,
          field: 'user',
          value: JSON.stringify({
            email: userData.email,
            name: `${userData.firstName || ''} ${userData.lastName || ''}`.trim(),
            role: userData.role,
            organization: orgHierarchy,
            note: 'Created without Cognito invitation due to network error'
          }),
          user: loggedInUser
        });
        
        // Show a warning that the email invitation wasn't sent
        alert('User created successfully, but the invitation email could not be sent. The user will need to be manually invited through AWS Cognito.');
      }
      
      // Notify parent component
      if (onUserAdded) {
        onUserAdded({
          ...userData,
          organizationID: finalOrgId
        });
      }
      
      // Close everything
      setShowUserModal(false);
      onClose();
    } catch (error) {
      console.error('Error creating user:', error);
      throw error;
    } finally {
      setSubmitting(false);
    }
  };

  // Get sorted organizations for display
  const getSortedOrganizations = () => {
    return Object.entries(organizations)
      .sort(([, a], [, b]) => {
        // Sort SYSTEM first, then alphabetically
        if (a.type === 'SYSTEM' || a.id === 'SYSTEM') return -1;
        if (b.type === 'SYSTEM' || b.id === 'SYSTEM') return 1;
        return (a.name || '').localeCompare(b.name || '');
      });
  };

  const steps = needsClientSelection() 
    ? ['Select Organization', 'Select Client'] 
    : ['Select Organization'];

  return (
    <>
      <Dialog 
        open={open && !showUserModal} 
        onClose={onClose}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Add New User - Select Organization</DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2 }}>
            {steps.length > 1 && (
              <Stepper activeStep={step} sx={{ mb: 4 }}>
                {steps.map((label) => (
                  <Step key={label}>
                    <StepLabel>{label}</StepLabel>
                  </Step>
                ))}
              </Stepper>
            )}
            
            <Stack spacing={3}>
              {step === 0 && (
                <>
                  <Typography variant="body2" color="text.secondary">
                    Select the organization where the new user will be created.
                  </Typography>
                  
                  <FormControl fullWidth required>
                    <InputLabel>Organization</InputLabel>
                    <Select
                      value={selectedOrganization}
                      onChange={(e) => {
                        setSelectedOrganization(e.target.value);
                        setSelectedClient(''); // Reset client selection
                      }}
                      label="Organization"
                    >
                      {getSortedOrganizations().map(([id, org]) => (
                        <MenuItem key={id} value={id}>
                          <Stack direction="row" spacing={1} alignItems="center">
                            <Typography>
                              {org.name}
                            </Typography>
                            {(org.type === 'SYSTEM' || org.id === 'SYSTEM') && (
                              <Typography variant="caption" color="primary">
                                (System)
                              </Typography>
                            )}
                            {(org.type === 'AGENCY' || org.isAgency || 
                              (org.name && org.name.toUpperCase().startsWith('AFFILIATE'))) && (
                              <Typography variant="caption" color="secondary">
                                (Affiliate)
                              </Typography>
                            )}
                          </Stack>
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                  
                  {selectedOrganization && needsClientSelection() && (
                    <Alert severity="info">
                      This is an affiliate organization. You can create users at the affiliate level or select a client.
                    </Alert>
                  )}
                </>
              )}
              
              {step === 1 && (
                <>
                  <Typography variant="body2" color="text.secondary">
                    {filteredClients.length > 0 
                      ? `Select a client under ${organizations[selectedOrganization]?.name} or leave empty to create user at the affiliate level.`
                      : `No clients found under ${organizations[selectedOrganization]?.name}. The user will be created at the affiliate level.`
                    }
                  </Typography>
                  
                  {filteredClients.length > 0 ? (
                    <FormControl fullWidth>
                      <InputLabel>Client (Optional)</InputLabel>
                      <Select
                        value={selectedClient}
                        onChange={(e) => setSelectedClient(e.target.value)}
                        label="Client (Optional)"
                      >
                        <MenuItem value="">
                          <em>Create at Affiliate Level</em>
                        </MenuItem>
                        {filteredClients.map((client) => {
                          const clientOrg = organizations[client.organizationID];
                          return (
                            <MenuItem key={client.id} value={client.organizationID}>
                              {clientOrg?.name || 'Unknown Client'}
                            </MenuItem>
                          );
                        })}
                      </Select>
                    </FormControl>
                  ) : (
                    <Alert severity="info">
                      User will be created at the affiliate level with Agency Admin role.
                    </Alert>
                  )}
                </>
              )}
            </Stack>
          </Box>
        </DialogContent>
        <DialogActions>
          {step > 0 && (
            <Button onClick={handleBack}>
              Back
            </Button>
          )}
          <Button onClick={onClose}>
            Cancel
          </Button>
          <Button 
            onClick={handleNext}
            variant="contained"
            disabled={!selectedOrganization}
          >
            Next
          </Button>
        </DialogActions>
      </Dialog>

      {/* User Details Modal */}
      {showUserModal && (
        <UserDetailsModal
          open={showUserModal}
          onClose={() => {
            setShowUserModal(false);
            onClose();
          }}
          onSubmit={handleUserSubmit}
          loading={submitting || loading}
          roles={getAvailableRoles()}
        />
      )}
    </>
  );
};

export default AddUserWithOrgDialog;